import { PerformanceBreakdownStat } from '../VaultMainContent/PerformanceBreakdownStat';
import { BigNum, QUOTE_PRECISION_EXP } from '@drift-labs/sdk';
import { useState } from 'react';
import { PublicKey } from '@solana/web3.js';
import { useEffect } from 'react';
import { useVaultsStore } from 'src/stores/vaultsStore/useVaultsStore';
import { useVaultStats } from 'src/hooks/query/vaults/derived/useVaultStats';
import { UiVaults } from 'src/constants/vaults';
import { UiVaultConfig } from 'src/@types/vaults';
import { UIMarket } from '@drift/common';
import { VaultPerformanceBreakdownForViewVaultPage } from '../VaultMainContent/VaultPerformance/VaultPerformanceBreakdown';
import { VaultStats } from '../VaultMainContent/VaultPerformance/VaultStats';
import { SHOW_VAULTS_ALL_TIME_EARNINGS } from '../../../constants/constants';
import { Open } from '@drift-labs/icons';
import Divider from 'src/components/primitives/Divider/Divider';
import Link from 'src/components/primitives/Link/Link';

const PerformanceBreakdownStats = (props: {
	vaultPubkey: string;
	marketSymbol: string;
}) => {
	const { data: vaultStats, isLoading: isVaultStatsLoading } = useVaultStats(
		props.vaultPubkey
	);
	const uiVaultConfig = UiVaults.getVaultConfig(props.vaultPubkey);
	const vaultClient = useVaultsStore((s) => s.vaultClient);

	const [totalPnl, setTotalPnl] = useState<BigNum | undefined>(undefined);

	useEffect(() => {
		if (vaultStats && uiVaultConfig && vaultClient) {
			if (uiVaultConfig.isNotionalGrowthStrategy) {
				vaultClient
					.getSubscribedVaultUser(
						new PublicKey(uiVaultConfig.driftUserPubkeyString)
					)
					.then((vaultUser) => {
						const vaultNetQuoteDeposits = vaultUser
							.getUserAccount()
							.totalDeposits.sub(vaultUser.getUserAccount().totalWithdraws);
						const currentNotionalPnl = vaultStats.tvlQuote.sub(
							BigNum.from(vaultNetQuoteDeposits, QUOTE_PRECISION_EXP)
						);

						setTotalPnl(currentNotionalPnl);
					});
			} else {
				setTotalPnl(vaultStats.totalBasePnl);
			}
		}
	}, [vaultStats, uiVaultConfig, vaultClient]);

	if (!uiVaultConfig) return null;

	return (
		<div className="flex w-full flex-col gap-y-md divide-stroke-divider sm:gap-4">
			<VaultStats
				vaultPubkey={props.vaultPubkey}
				depositAssetMarketIndex={uiVaultConfig.depositAsset}
			/>
			<div className="flex flex-col gap-y-2xl rounded-sm bg-container-bg p-4 md:p-6">
				<div className="mb-0 flex flex-col gap-y-lg sm:gap-sm">
					<div className="flex w-full flex-row justify-between">
						<span className="typo-t2">Performance Breakdown</span>
						<Link
							variant="secondary"
							className="typo-b3 whitespace-nowrap"
							href={`/?authority=${uiVaultConfig.vaultPubkeyString}`}
							rightIcon={Open}
							openInNewTab
						>
							View <span className="hidden sm:inline">Vault</span> Activities
						</Link>
					</div>
					<div className="flex w-full flex-col justify-between gap-y-md sm:flex-row">
						{SHOW_VAULTS_ALL_TIME_EARNINGS && (
							<PerformanceBreakdownStat
								label="Total Earnings (All)"
								value={`${totalPnl?.prettyPrint(true)}`}
								loading={isVaultStatsLoading}
								marketSymbol={props.marketSymbol}
								tooltip="The total earnings for the entire vault. Total Earnings = Total Vault TVL - Total Net Deposits."
							/>
						)}
						<PerformanceBreakdownStat
							label="Max Daily Drawdown"
							value={`${vaultStats?.maxDrawdownPct.toFixed(2)}%`}
							loading={isVaultStatsLoading}
							tooltip={"The maximum day-to-day loss in the vault's history."}
						/>
						<PerformanceBreakdownStat
							label="30D Trading Volume (USD)"
							value={`$${vaultStats?.volume30Days.toMillified()}`}
							loading={isVaultStatsLoading}
						/>
					</div>
				</div>
				<Divider />
				<VaultPerformanceBreakdownForViewVaultPage
					vaultPubkey={props.vaultPubkey}
					depositAssetMarketIndex={uiVaultConfig.depositAsset}
				/>
			</div>
		</div>
	);
};

export const VaultPerformanceTabContent = (props: {
	uiVaultConfig: UiVaultConfig;
}) => {
	const marketSymbol = props.uiVaultConfig.isNotionalGrowthStrategy
		? 'USDC'
		: UIMarket.createSpotMarket(props.uiVaultConfig.depositAsset).market.symbol;

	return (
		<div className="flex flex-col gap-4">
			<PerformanceBreakdownStats
				vaultPubkey={props.uiVaultConfig.vaultPubkeyString}
				marketSymbol={marketSymbol}
			/>
		</div>
	);
};
